import * as owaspPasswordStrengthTest from 'owasp-password-strength-test';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;
const MIN_OPTIONAL_TESTS_FOR_MEDIUM = 2;
const OPTIONAL_TESTS_FOR_STRONG = 4;

export type PasswordStrengthLevel = 'too-short' | 'weak' | 'medium' | 'strong';
export type PasswordPolicyErrorCode = 'PasswordTooShort' | 'PasswordTooWeak';

export interface PasswordPolicyResult {
  errorCode: null | PasswordPolicyErrorCode;
  isAccepted: boolean;
  level: PasswordStrengthLevel;
}

type OwaspPasswordStrengthApi = {
  config: typeof owaspPasswordStrengthTest.config;
  test: typeof owaspPasswordStrengthTest.test;
};

const passwordStrengthApi: OwaspPasswordStrengthApi = (() => {
  const direct = owaspPasswordStrengthTest as Partial<OwaspPasswordStrengthApi> & {
    default?: Partial<OwaspPasswordStrengthApi>;
  };

  if (typeof direct.config === 'function' && typeof direct.test === 'function') {
    return {
      config: direct.config.bind(direct),
      test: direct.test.bind(direct),
    };
  }

  if (typeof direct.default?.config === 'function' && typeof direct.default?.test === 'function') {
    return {
      config: direct.default.config.bind(direct.default),
      test: direct.default.test.bind(direct.default),
    };
  }

  throw new Error('OWASP password strength API is not available');
})();

function configureOwasp(): void {
  passwordStrengthApi.config({
    allowPassphrases: true,
    maxLength: PASSWORD_MAX_LENGTH,
    minLength: PASSWORD_MIN_LENGTH,
    minOptionalTestsToPass: MIN_OPTIONAL_TESTS_FOR_MEDIUM,
  });
}

export function evaluatePasswordStrength(password: string): PasswordStrengthLevel {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return 'too-short';
  }

  configureOwasp();
  const result = passwordStrengthApi.test(password);

  if (result.requiredTestErrors.length > 0) {
    return 'weak';
  }

  if (result.isPassphrase) {
    return 'strong';
  }

  if (result.optionalTestsPassed < MIN_OPTIONAL_TESTS_FOR_MEDIUM) {
    return 'weak';
  }

  if (result.optionalTestsPassed >= OPTIONAL_TESTS_FOR_STRONG) {
    return 'strong';
  }

  return 'medium';
}

export function validatePasswordPolicy(password: string): PasswordPolicyResult {
  const level = evaluatePasswordStrength(password);

  if (level === 'too-short') {
    return { level, isAccepted: false, errorCode: 'PasswordTooShort' };
  }

  if (level === 'weak') {
    return { level, isAccepted: false, errorCode: 'PasswordTooWeak' };
  }

  return { level, isAccepted: true, errorCode: null };
}
